import { RadioGroup, RadioGroupItem } from '@neo/primitives/base/radio-group'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Switch } from '@neo/primitives/switch'
import { NodevarList } from '../nodevar-list'

export type RecordingMode = 'always' | 'dynamic'
export type RecordingOption = 'all_separate' | 'customer_and_agent' | 'all_together'

export interface RecordingConfigValue {
  record: boolean
  record_mode?: RecordingMode
  record_condition?: string
  recording_option?: RecordingOption
}

export interface RecordingConfigProps {
  value: RecordingConfigValue
  onChange: (value: RecordingConfigValue) => void
  nodeVars?: string[]
  showRecordingOptions?: boolean
  className?: string
  configId?: string
}

const recordingModeOptions = [
  { value: 'always', label: 'Always' },
  { value: 'dynamic', label: 'Dynamic' },
]

const recordingOptions = [
  { value: 'all_separate', label: 'All separate' },
  { value: 'customer_and_agent', label: 'Customer and agent' },
  { value: 'all_together', label: 'All together' },
]

export const RecordingConfig = ({
  value,
  onChange,
  nodeVars = [],
  showRecordingOptions = false,
  className,
  configId,
}: RecordingConfigProps) => {
  const recordMode = value.record_mode ?? 'always'
  const isDynamic = recordMode === 'dynamic'

  const handleModeChange = (mode: RecordingMode) => {
    onChange({
      ...value,
      record: true,
      record_mode: mode,
    })
  }

  const handleConditionChange = (condition: string) => {
    onChange({
      ...value,
      record_condition: condition,
    })
  }

  const handleRecordingOptionChange = (option: RecordingOption) => {
    onChange({
      ...value,
      recording_option: option,
    })
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label>Recording</Label>
        <Switch
          value={value.record}
          onChange={(checked: boolean) => onChange({ ...value, record: checked })}
          data-testid="switch-recording-enabled"
        />
      </div>

      {value.record && (
        <>
          <RadioGroup
            name="recording-mode"
            value={recordMode}
            onValueChange={(v) => handleModeChange(v as RecordingMode)}
            className="flex gap-4 mb-3"
          >
            {recordingModeOptions.map((option) => (
              <div key={option.value} className="flex items-center gap-2">
                <RadioGroupItem
                  value={option.value}
                  id={`record-mode-${option.value}`}
                  data-testid={`radio-record-mode-${option.value}`}
                />
                <Label htmlFor={`record-mode-${option.value}`} className="cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {isDynamic && (
            <div className="mb-3">
              <Label className="mb-1.5 block text-s-400">Condition</Label>
              <Input
                value={value.record_condition ?? ''}
                onChange={(e) => handleConditionChange(e.target.value)}
                placeholder="Enter condition"
                inputClassName="font-jetbrains text-xs-400"
                endIcon={
                  <NodevarList
                    nodevars={nodeVars}
                    className="pr-2"
                    inputElementSelector={
                      configId ? `[data-record-condition="${configId}"]` : '[name=record_condition]'
                    }
                  />
                }
                name="record_condition"
                data-record-condition={configId}
                data-testid="input-record-condition"
              />
            </div>
          )}

          {showRecordingOptions && (
            <div className="mt-3">
              <Label className="mb-2 block text-s-400">Recording option</Label>
              <RadioGroup
                name="recording-option"
                value={value.recording_option ?? 'all_together'}
                onValueChange={(v) => handleRecordingOptionChange(v as RecordingOption)}
                className="flex flex-col gap-2"
              >
                {recordingOptions.map((option) => (
                  <div key={option.value} className="flex items-center gap-2">
                    <RadioGroupItem
                      value={option.value}
                      id={`recording-option-${option.value}`}
                      data-testid={`radio-recording-option-${option.value}`}
                    />
                    <Label htmlFor={`recording-option-${option.value}`} className="cursor-pointer">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}
        </>
      )}
    </div>
  )
}
